import { singleton, inject } from "tsyringe";
import { BackendClient } from "./backend-client";
import type { ViewportQueryRequest, ViewportQueryResponse } from "./viewport-query.types";

/**
 * API client for the viewport-query endpoint.
 * Handles fetching crosspoint data for grid viewports with configurable axes.
 */
@singleton()
export class ViewportQueryClient {

    private static readonly ENDPOINT = '/api/v1/viewport-query';

    constructor(
        @inject(BackendClient) private readonly backendClient: BackendClient
    ) {}

    /**
     * Fetches crosspoint data for a given viewport configuration.
     *
     * @param request - The viewport query request parameters
     * @param signal - Optional AbortSignal for request cancellation
     * @returns Promise resolving to the viewport query response
     * @throws Error if the request fails or returns invalid data
     */
    public async query<TItemDTO = unknown>(
        request: ViewportQueryRequest,
        signal?: AbortSignal
    ): Promise<ViewportQueryResponse<TItemDTO>> {
        const response = await this.backendClient.post<ViewportQueryResponse<TItemDTO>>(
            ViewportQueryClient.ENDPOINT,
            true,
            {
                xAxis: request.xAxis,
                yAxis: request.yAxis,
                includes: request.includes
            },
            signal
        );

        if (!response) {
            throw new Error(`Viewport query request failed: No response received`);
        }

        return response;
    }
}
